/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, VStack, Text, Divider } from '@chakra-ui/react';
import { UserPRPTextComparisonComponent } from '@karya/core';
import { ParsedParams } from '@karya/generic-task-react';
import { ComponentRendererProps } from '../ComponentRenderer';

export function UserPRPTextComparisonRenderer(props: ComponentRendererProps) {
  const typedParams = props.node.params as ParsedParams<UserPRPTextComparisonComponent>;
  const component = props.node.component as UserPRPTextComparisonComponent;

  const modelAName = typedParams.model_a_name ?? 'Model A';
  const modelAResponse = typedParams.model_a_response ?? 'Sample response from Model A will appear here...';
  const modelBName = typedParams.model_b_name ?? 'Model B';
  const modelBResponse = typedParams.model_b_response ?? 'Sample response from Model B will appear here...';

  const selectedModel = typedParams.selected_model;

  const handleSelectModel = (modelName: string) => {
    if (!props.isCurrent) {
      return;
    }
    props.writeOutput(component.selected_model, modelName);
  };

  return (
    <VStack spacing="16px" width="100%" alignItems="stretch">
      <Box
        border="2px solid"
        borderColor={selectedModel === modelAName ? 'gray.400' : 'gray.200'}
        borderRadius="8px"
        p="16px"
        cursor={props.isCurrent ? 'pointer' : 'default'}
        _hover={props.isCurrent ? { borderColor: 'gray.300', bg: 'gray.50' } : {}}
        onClick={() => handleSelectModel(modelAName)}
        transition="all 0.2s"
      >
        <Text fontWeight="bold" mb="8px" fontSize="sm">
          {modelAName}
        </Text>
        <Text fontSize="xs" color="gray.600" noOfLines={3}>
          {modelAResponse}
        </Text>
      </Box>

      <Divider />

      <Box
        border="2px solid"
        borderColor={selectedModel === modelBName ? 'gray.400' : 'gray.200'}
        borderRadius="8px"
        p="16px"
        cursor={props.isCurrent ? 'pointer' : 'default'}
        _hover={props.isCurrent ? { borderColor: 'gray.300', bg: 'gray.50' } : {}}
        onClick={() => handleSelectModel(modelBName)}
        transition="all 0.2s"
      >
        <Text fontWeight="bold" mb="8px" fontSize="sm">
          {modelBName}
        </Text>
        <Text fontSize="xs" color="gray.600" noOfLines={3}>
          {modelBResponse}
        </Text>
      </Box>
    </VStack>
  );
}
